import {
	CameraControls,
	Center,
	Html,
	Image,
	KeyboardControls,
	type KeyboardControlsEntry,
	PerspectiveCamera,
	Sphere,
	useCursor,
	useKeyboardControls,
	useTexture,
} from '@react-three/drei'
import { Canvas, useFrame, useThree } from '@react-three/fiber'
import React, { Suspense, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router'
import { BackSide, LinearFilter, MathUtils } from 'three'
import chevronImage from '#app/assets/chevron.png?url'
import Spinner from '#app/components/spinner.tsx'
import { cn } from '#app/lib/utils'
import { type HeadingLink } from '#app/types/capture.ts'
import { floatToBase62 } from '#app/utils/base62.ts'
import { get360ImgSrc } from '#app/utils/misc.tsx'

const imageOrientation = 1
const loaderHook = useTexture // imageExtension === 'ktx2' ? useKTX2 : useTexture

interface ThreeSixtyImageProps {
	className?: string
	imageId: string | undefined
	captureId: string
	links: HeadingLink[]
	azimuthAngle?: number
	polarAngle?: number
	onViewMoved: (
		azimuthAngle: number | undefined,
		polarAngle: number | undefined,
	) => void
}

interface ThreeSixtyImageContentProps extends ThreeSixtyImageProps {}

const degreesToRadians = (deg: number) => (deg * Math.PI) / 180

const Sphere360: React.FC<{
	imageUrl: string
}> = ({ imageUrl }) => {
	const texture = loaderHook(imageUrl)

	texture.minFilter = LinearFilter

	return (
		<mesh scale={[-1, imageOrientation, 1]}>
			<sphereGeometry args={[500, 60, 40]} />
			<meshBasicMaterial map={texture} side={BackSide} depthWrite={false} />
		</mesh>
	)
}

function getRotateSpeed(zoomLevel: number) {
	return -0.6 / zoomLevel
}

enum Controls {
	forward = 'forward',
	back = 'back',
	left = 'left',
	right = 'right',
}

const ThreeSixtyImage: React.FC<ThreeSixtyImageProps> = (props) => {
	const map = useMemo<KeyboardControlsEntry<Controls>[]>(
		() => [
			{ name: Controls.forward, keys: ['ArrowUp'] },
			{ name: Controls.back, keys: ['ArrowDown'] },
			{ name: Controls.left, keys: ['ArrowLeft'] },
			{ name: Controls.right, keys: ['ArrowRight'] },
		],
		[],
	)

	return (
		<Canvas
			data-testid="360-image"
			gl={{ antialias: true }}
			className={cn(
				'cursor-grab rounded-md active:cursor-grabbing',
				props.className,
			)}
		>
			<Suspense
				fallback={
					<Html center>
						<Spinner />
					</Html>
				}
			>
				<KeyboardControls map={map}>
					<ThreeSixtyImageContent {...props} />
				</KeyboardControls>
			</Suspense>
		</Canvas>
	)
}

function getAngularDifference(angle1: number, angle2: number): number {
	const difference = Math.abs(angle1 - angle2) % 360
	return difference > 180 ? 360 - difference : difference
}

const ThreeSixtyImageContent: React.FC<ThreeSixtyImageContentProps> = ({
	captureId,
	imageId,
	links,
	azimuthAngle,
	polarAngle,
	onViewMoved,
}) => {
	const navigate = useNavigate()
	const cameraControls = useRef<CameraControls>(null)

	const selectedImageUrl = useMemo(() => {
		if (!imageId) return null

		const width = window.innerWidth * (window.devicePixelRatio || 1)
		const dpr = window.devicePixelRatio || 1

		return get360ImgSrc(captureId, imageId, width, dpr)
	}, [captureId, imageId])

	const [sub, get] = useKeyboardControls<Controls>()

	useEffect(() => {
		return sub(
			(state) => state.forward,
			(pressed) => {
				if (pressed) {
					const cameraControl = cameraControls.current
					if (!cameraControl) return

					cameraControl.normalizeRotations()
					const closestLink = findClosestLink(
						links,
						MathUtils.radToDeg(cameraControl.azimuthAngle),
					)

					if (closestLink) {
						void navigate(
							`${closestLink.link.href}?azimuth-angle=${floatToBase62(cameraControl.azimuthAngle)}&polar-angle=${floatToBase62(cameraControl.polarAngle)}`,
						)
					}
				}
			},
		)
	}, [links, navigate, sub])

	useEffect(() => {
		return sub(
			(state) => state.back,
			(pressed) => {
				if (pressed) {
					const cameraControl = cameraControls.current
					if (!cameraControl) return

					cameraControl.normalizeRotations()
					const closestLink = findClosestLink(
						links,
						MathUtils.radToDeg(cameraControl.azimuthAngle) + 180,
					)

					if (closestLink) {
						void navigate(
							`${closestLink.link.href}?azimuth-angle=${floatToBase62(cameraControl.azimuthAngle)}&polar-angle=${floatToBase62(cameraControl.polarAngle)}`,
						)
					}
				}
			},
		)
	}, [links, navigate, sub])

	useEffect(() => {
		return sub(
			(state) => state.left,
			(pressed) => {
				if (!pressed) {
					const cameraControl = cameraControls.current
					if (!cameraControl) return

					cameraControl.normalizeRotations()

					onViewMoved(cameraControl.azimuthAngle, cameraControl.polarAngle)
				}
			},
		)
	}, [onViewMoved, sub])

	useEffect(() => {
		return sub(
			(state) => state.right,
			(pressed) => {
				if (!pressed) {
					const cameraControl = cameraControls.current
					if (!cameraControl) return

					cameraControl.normalizeRotations()

					onViewMoved(cameraControl.azimuthAngle, cameraControl.polarAngle)
				}
			},
		)
	}, [onViewMoved, sub])

	useFrame((_, delta) => {
		const cameraControl = cameraControls.current
		if (!cameraControl) return

		if (get().left) {
			void cameraControl.rotate(delta, 0)
		}
		if (get().right) {
			void cameraControl.rotate(-delta, 0)
		}
	})

	return (
		<>
			{/* Links are full page URLs - no need for image prefetching here */}
			{selectedImageUrl && <Sphere360 imageUrl={selectedImageUrl} />}
			<PerspectiveCamera makeDefault position={[0, 0, 1e-5]} fov={100} />
			<CameraControls
				draggingSmoothTime={0}
				minDistance={1e-5}
				maxDistance={1e-5}
				azimuthAngle={azimuthAngle}
				polarAngle={polarAngle}
				ref={cameraControls}
				mouseButtons={{
					// Not ideal to use magic numbers, but I'm having issues importing the ACTION as expected, so work around it for now to keep moving
					left: 1,
					middle: 0,
					right: 0,
					wheel: 16,
				}}
				minZoom={0.75}
				maxZoom={16}
				onStart={(event: { type: 'controlstart' } | undefined) => {
					const startEvent = event as unknown as {
						target: CameraControls
					}

					startEvent.target.azimuthRotateSpeed = getRotateSpeed(
						startEvent.target.camera.zoom,
					)
					startEvent.target.polarRotateSpeed = getRotateSpeed(
						startEvent.target.camera.zoom,
					)
				}}
				onEnd={(event: { type: 'controlend' } | undefined) => {
					const endEvent = event as unknown as {
						target: CameraControls
					}
					endEvent.target.normalizeRotations()
					onViewMoved(endEvent.target.azimuthAngle, endEvent.target.polarAngle)
				}}
			/>
			{links.map((link) => {
				return (
					<TeleportIndicator
						key={link.href}
						link={link}
						navigateToLink={() => {
							void navigate(
								`${link.href}?azimuth-angle=${floatToBase62(cameraControls.current?.azimuthAngle)}&polar-angle=${floatToBase62(cameraControls.current?.polarAngle)}`,
							)
						}}
					/>
				)
			})}
			<Sphere args={[0.1, 32, 32]} position={[0, 0, 0]} />
		</>
	)
}

function TeleportIndicator({
	link,
	navigateToLink,
}: {
	link: HeadingLink
	navigateToLink: () => void
}) {
	const [hovered, setHovered] = React.useState(false)
	const parentContainer = useThree((state) => state.gl.domElement)
	// @ts-expect-error - useCursor doesn't allow passing null, but doing so allows the cursor to correctly revert back to its previous setting
	useCursor(hovered, 'pointer', null, parentContainer)

	return (
		<group rotation-y={degreesToRadians(link.heading)}>
			<Center
				position={[0, -5, -10]}
				rotation={[-Math.PI / 2, 0, 0]}
				scale={3.5}
			>
				<Image
					url={chevronImage}
					transparent
					opacity={0.75}
					scale={hovered ? 1.1 : 1}
					onClick={navigateToLink}
					onPointerEnter={() => {
						setHovered(true)
					}}
					onPointerLeave={() => {
						setHovered(false)
					}}
				/>
			</Center>
		</group>
	)
}

export default ThreeSixtyImage

function findClosestLink(
	links: HeadingLink[],
	currentHeadingInDegrees: number,
) {
	return links
		.map((link) => {
			const angleDiff = getAngularDifference(
				currentHeadingInDegrees,
				link.heading,
			)
			return { link, angleDiff }
		})
		.reduce((closest, current) =>
			current.angleDiff < closest.angleDiff ? current : closest,
		)
}
